import { TFunction } from 'i18next';

import {
  AiInstructionsTranscriptComponent,
  ArticleCardTranscriptComponent,
  AutoflowToolTranscriptComponent,
  ContentSnippetTranscriptComponent,
  CustomEventTranscriptComponent,
  ExecutionErrorTranscriptComponent,
  ExecutionTranscriptComponent,
  GotoWorkflowTranscriptComponent,
  IntentTriggeredTranscriptComponent,
  RelatedArticlesTranscriptComponent,
  SolveABTestVariantAssignmentTranscriptComponent,
  TranscriptComponent,
} from '../../ConversationTranscript/types';
import {
  AuditEvent,
  AuditEventSeverity,
  AuditExchange,
  AuditSource,
  ConversationAudit,
  ConversationAuditState,
  ToolCallStatus,
} from './auditData';
import {
  getExchangeLatencyMs,
  parseComponentTimestamp,
  splitIntoExchanges,
  toIsoUtcTimestamp,
  TranscriptExchange,
} from './exchanges';
import {
  isErrorLogSeverity,
  resolveErrorDescription,
} from 'src/pages/events/error/utils';
import { ApiEventQueryEvent } from 'src/types/ApiEventTypes';
import { BaseErrorPayload, ErrorQueryEvent } from 'src/types/ErrorEventTypes';

type T = TFunction<'intent-conversation-analytics'>;

// What the error service returns for a message it carries no description for.
const NOT_APPLICABLE = 'N/A';

const capitalizeFirst = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const titleCase = (value: string) =>
  value.replace(/\b[a-z]/g, char => char.toUpperCase());

// "_Offers__Offer_Type_Clarifier" -> "Offers - Offer type clarifier"
// set_context_variable -> "Set context variable"
const humanizeToolName = (toolName: string): string => {
  const trimmed = toolName.replace(/^_+|_+$/g, '');
  const bracket = trimmed.match(/^(.+?)__+(.+)$/);
  if (bracket) {
    const category = titleCase(bracket[1].replace(/_+/g, ' ').trim());
    const rest = capitalizeFirst(
      bracket[2].replace(/_+/g, ' ').trim().toLowerCase(),
    );
    return `${category} - ${rest}`;
  }
  return capitalizeFirst(trimmed.replace(/_+/g, ' ').trim().toLowerCase());
};

// Execution internals the transcript renders as evidence rather than as a
// message bubble. A component is a visible message iff its type is NOT in this
// list; the audit drawer and the exchange-merge helper both key off it so the
// "what's a message, what's an event" line stays in one place.
export const EVENT_MESSAGE_TYPES = [
  'article_card',
  'ai_instructions',
  'autoflow_tool',
  'chat_handoff_status_message',
  'content_snippet',
  'custom_event',
  'execution',
  'execution_error',
  'goto_workflow',
  'indicator',
  'intent_trigger',
  'related_articles',
  'solve_ab_test_variant_assignment',
];

// The failure statuses tools actually return in their output.
const TOOL_ERROR_STATUSES = ['error', 'fail'];

// Tool outputs carry no status field; a few tools report failure in-band.
// An empty output says nothing either way.
const getToolCallStatus = (output: unknown): ToolCallStatus | undefined => {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return undefined;
  }
  const record = output as Record<string, unknown>;
  if (Object.keys(record).length === 0) {
    return undefined;
  }
  return typeof record.status === 'string' &&
    TOOL_ERROR_STATUSES.includes(record.status)
    ? 'error'
    : 'completed';
};

const toSeverity = (severity: string | null): AuditEventSeverity | undefined =>
  isErrorLogSeverity(severity) ? severity : undefined;

const toSources = (component: TranscriptComponent): AuditSource[] => {
  if (component.message_type === 'related_articles') {
    const { articles } = component as RelatedArticlesTranscriptComponent;
    return articles.map(article => ({
      documentId: article.document_id,
      link: article.link,
      title: article.title,
    }));
  }
  const article = component as ArticleCardTranscriptComponent;
  return [
    {
      documentId: article.title,
      link: article.link,
      title: article.title,
    },
  ];
};

// One event per recorded transcript component. `detail` is the component's own
// text wherever it has any; nothing here paraphrases a payload.
const toTranscriptEvent = (
  t: T,
  component: TranscriptComponent,
): AuditEvent | null => {
  const base = {
    latencyMs: null,
    timestamp: toIsoUtcTimestamp(component.timestamp),
  };

  switch (component.message_type) {
    case 'intent_trigger':
    case 'goto_workflow': {
      const {
        intent_title: intentTitle,
        intent_workflow_id: workflowId,
        intent_workflow_version: workflowVersion,
      } = component as
        | IntentTriggeredTranscriptComponent
        | GotoWorkflowTranscriptComponent;
      if (!intentTitle) {
        return null;
      }
      const reasoning = (component as IntentTriggeredTranscriptComponent)
        .reasoning;
      return {
        ...base,
        detail: t('conversationAudit.detailIntent', { intent: intentTitle }),
        evidence: reasoning ?? undefined,
        kind: 'intent',
        workflowLink: workflowId
          ? { intentTitle, version: workflowVersion, workflowId }
          : undefined,
      };
    }
    case 'related_articles':
    case 'article_card': {
      const sources = toSources(component);
      if (!sources.length) {
        return null;
      }
      return {
        ...base,
        detail: t('conversationAudit.detailRetrieval', {
          count: sources.length,
        }),
        kind: 'retrieval',
        sources,
      };
    }
    case 'autoflow_tool': {
      const {
        input,
        output,
        tool_name: toolName,
      } = component as AutoflowToolTranscriptComponent;
      const displayToolName = humanizeToolName(toolName);
      return {
        ...base,
        detail: t('conversationAudit.detailTool', { tool: displayToolName }),
        evidence: displayToolName,
        kind: 'tool',
        payload: {
          request: input,
          response: output,
          toolCall: { status: getToolCallStatus(output) },
        },
      };
    }
    case 'custom_event': {
      const { event_configuration_name: eventName, properties } =
        component as CustomEventTranscriptComponent;
      return {
        ...base,
        detail: t('conversationAudit.detailCustomEvent', { event: eventName }),
        kind: 'step',
        payload: { request: properties },
      };
    }
    case 'ai_instructions': {
      const { ai_instruction_ids: ids } =
        component as AiInstructionsTranscriptComponent;
      return {
        ...base,
        aiInstructionIds: ids,
        detail: t('conversationAudit.detailAiInstructions', {
          count: ids.length,
        }),
        kind: 'step',
      };
    }
    case 'content_snippet': {
      const { content_snippet_ids: ids } =
        component as ContentSnippetTranscriptComponent;
      return {
        ...base,
        detail: t('conversationAudit.detailContentSnippets', {
          count: ids.length,
        }),
        kind: 'step',
      };
    }
    case 'solve_ab_test_variant_assignment': {
      const {
        ab_test_name: abTestName,
        is_control: isControl,
        variant_name: variantName,
      } = component as SolveABTestVariantAssignmentTranscriptComponent;
      return {
        ...base,
        detail: t('conversationAudit.detailAbTest', {
          variant:
            variantName ??
            (isControl
              ? t('conversationAudit.abTestControl')
              : t('conversationAudit.abTestVariant')),
        }),
        evidence: abTestName,
        kind: 'step',
      };
    }
    case 'execution_error': {
      const { message } = component as ExecutionErrorTranscriptComponent;
      return {
        ...base,
        detail: message ?? t('conversationAudit.detailUnknownError'),
        kind: 'error',
      };
    }
    case 'chat_handoff_status_message': {
      const { message, success } = component as ExecutionTranscriptComponent;
      if (success) {
        return {
          ...base,
          detail: t('conversationAudit.detailHandoffSuccess'),
          kind: 'step',
        };
      }
      // A recorded failure carries its own message (redacted where it had PII);
      // the category is what the legacy drawer showed for the same outcome.
      return {
        ...base,
        category: t('conversationAudit.detailHandoffFailed'),
        detail: message ?? '',
        kind: 'error',
      };
    }
    case 'execution':
    case 'indicator': {
      const { message } = component as ExecutionTranscriptComponent;
      if (!message) {
        return null;
      }
      return { ...base, detail: message, kind: 'step' };
    }
    default:
      return null;
  }
};

// A recorded error event, rendered the way the Log page renders it: the
// catalog message as the category, the service's own description as the
// detail, and the whole event kept so the severity, payload and How-to-fix
// components can read it directly.
const toErrorEvent = (t: T, error: ErrorQueryEvent): AuditEvent => {
  const payload = error.error_payload as BaseErrorPayload | null;
  const requestedUrl = (payload as { requested_url?: string } | null)
    ?.requested_url;
  const httpMethod = (payload as { http_method?: string } | null)?.http_method;
  const httpStatus =
    (payload as { http_status_code?: number } | null)?.http_status_code ??
    error.status_code;
  const responseBody = (payload as { response_body?: unknown } | null)
    ?.response_body;
  const isApiCall = error.error_type === 'API_CALL_FAILED' || !!requestedUrl;
  // The error service returns its N/A placeholder for a message it has no
  // description for. Carrying that through would render as "Failed — N/A", so
  // it is treated as the absence it is and the category speaks alone.
  const description = resolveErrorDescription(
    error.error_message,
    error.product,
  );
  const hasDescription = Boolean(description) && description !== NOT_APPLICABLE;

  return {
    category: error.error_message,
    detail: hasDescription ? (description as string) : '',
    error,
    evidence: requestedUrl
      ? [httpMethod, requestedUrl, httpStatus].filter(Boolean).join(' · ')
      : undefined,
    kind: isApiCall ? 'api' : 'error',
    latencyMs: null,
    payload: isApiCall
      ? {
          request: {
            method: (payload as { http_method?: string } | null)?.http_method,
            url: requestedUrl,
          },
          response: responseBody,
          responseAbsence: responseBody ? undefined : 'not_captured',
        }
      : undefined,
    severity: toSeverity(error.severity),
    timestamp: toIsoUtcTimestamp(error.timestamp),
  };
};

// Errors arrive on their own timeline, so each is placed in the exchange whose
// components bracket it, falling back to the last exchange that had already
// started. Matching on time is inference; an explicit turn id on the event is
// what would remove it.
const findExchangeForTimestamp = (
  exchanges: TranscriptExchange[],
  timestamp: string,
) => {
  const at = parseComponentTimestamp(timestamp);
  if (!Number.isFinite(at) || exchanges.length === 0) {
    return Math.max(exchanges.length - 1, 0);
  }
  let match = 0;
  exchanges.forEach((exchange, index) => {
    const start = parseComponentTimestamp(
      exchange.components[0].component.timestamp,
    );
    if (start <= at) {
      match = index;
    }
  });
  return match;
};

// An autoflow_error component and a recorded error event can describe the same
// failure. The recorded event is the richer of the two (severity, error id, how
// to fix), so a component error is dropped where one covers it.
const isCoveredByErrorEvent = (event: AuditEvent, errorEvents: AuditEvent[]) =>
  errorEvents.some(recorded => {
    const gap = Math.abs(
      parseComponentTimestamp(recorded.timestamp) -
        parseComponentTimestamp(event.timestamp),
    );
    return gap <= 30_000;
  });

const getState = (
  transcriptComponents: TranscriptComponent[],
  errorTimestamps: number[],
): ConversationAuditState => {
  if (errorTimestamps.length === 0) {
    return 'healthy';
  }
  const lastError = Math.max(...errorTimestamps);
  const answeredAfterFailure = transcriptComponents.some(
    component =>
      component.component_type === 'output' &&
      parseComponentTimestamp(component.timestamp) >= lastError,
  );
  return answeredAfterFailure ? 'partial_failure' : 'unresolved';
};

export const buildConversationAudit = ({
  apiEvents,
  errorEvents,
  t,
  transcriptComponents,
}: {
  apiEvents: ApiEventQueryEvent[];
  errorEvents: ErrorQueryEvent[];
  t: T;
  transcriptComponents: TranscriptComponent[];
}): ConversationAudit | null => {
  if (!transcriptComponents.length) {
    return null;
  }

  const transcriptExchanges = splitIntoExchanges(transcriptComponents);

  // The api-events endpoint is a filtered view of the same error stream, so an
  // api event already present as an error event would double count.
  const recordedErrorIds = new Set(errorEvents.map(error => error.error_id));
  const recordedErrors: ErrorQueryEvent[] = [
    ...errorEvents,
    ...apiEvents
      .filter(apiEvent => !recordedErrorIds.has(apiEvent.error_id))
      .map(apiEvent => ({
        ...apiEvent,
        severity: null,
        user_impact: undefined,
      })),
  ];

  const errorAuditEvents = recordedErrors.map(error => toErrorEvent(t, error));

  const exchanges: AuditExchange[] = transcriptExchanges.map(exchange => ({
    events: exchange.components
      .filter(({ component }) =>
        EVENT_MESSAGE_TYPES.includes(component.message_type),
      )
      .map(({ component }) => toTranscriptEvent(t, component))
      .filter((event): event is AuditEvent => event !== null)
      .filter(
        event =>
          event.kind !== 'error' ||
          !isCoveredByErrorEvent(event, errorAuditEvents),
      ),
    latencyMs: getExchangeLatencyMs(exchange),
  }));

  errorAuditEvents.forEach(event => {
    const index = findExchangeForTimestamp(
      transcriptExchanges,
      event.timestamp,
    );
    exchanges[index]?.events.push(event);
  });

  exchanges.forEach(exchange => {
    exchange.events.sort(
      (first, second) =>
        parseComponentTimestamp(first.timestamp) -
        parseComponentTimestamp(second.timestamp),
    );
  });

  // Latency is the gap from the previous recorded event of the same exchange —
  // the archive stores when a step landed, not how long it ran, so the first
  // event of an exchange measures from the visitor's message.
  exchanges.forEach((exchange, index) => {
    const opening =
      transcriptExchanges[index].components[0]?.component.timestamp;
    exchange.events.forEach((event, eventIndex) => {
      const previous =
        eventIndex === 0 ? opening : exchange.events[eventIndex - 1].timestamp;
      if (!previous) {
        return;
      }
      const latency =
        parseComponentTimestamp(event.timestamp) -
        parseComponentTimestamp(previous);
      event.latencyMs =
        Number.isFinite(latency) && latency > 0 ? latency : null;
    });
  });

  // Every surviving error — recorded events and component-derived ones alike —
  // is now in an exchange, so the state agrees with the red events the
  // transcript actually shows.
  const failureTimestamps = exchanges
    .flatMap(({ events }) => events)
    .filter(event => event.kind === 'error')
    .map(event => parseComponentTimestamp(event.timestamp));

  return {
    exchanges,
    state: getState(transcriptComponents, failureTimestamps),
  };
};
